import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import apiClient from '@/lib/api-client'

/**
 * Handles OAuth redirect: expects tokens in query string or fetches callback payload.
 * Backend SocialAuthController returns JSON; for browser redirects we accept
 * access_token/refresh_token query params when the callback page is used as redirect URI.
 */
export default function AuthCallback() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const run = async () => {
      const accessToken = searchParams.get('access_token')
      const refreshToken = searchParams.get('refresh_token')
      const provider = searchParams.get('provider') || 'google'
      const code = searchParams.get('code')

      try {
        if (accessToken && refreshToken) {
          localStorage.setItem('access_token', accessToken)
          localStorage.setItem('refresh_token', refreshToken)
          navigate('/dashboard', { replace: true })
          return
        }

        if (code) {
          const response = await apiClient.get<{
            data: {
              access_token: string
              refresh_token: string
              is_new_user?: boolean
            }
          }>(`/auth/social/${provider}/callback`, {
            params: Object.fromEntries(searchParams.entries()),
          })
          const data = response.data?.data
          if (data?.access_token && data?.refresh_token) {
            localStorage.setItem('access_token', data.access_token)
            localStorage.setItem('refresh_token', data.refresh_token)
            navigate(data.is_new_user ? '/dashboard/billing/subscription' : '/dashboard', {
              replace: true,
            })
            return
          }
        }

        setError('Authentication failed. Missing tokens.')
      } catch (err: any) {
        setError(err.response?.data?.error?.message || err.message || 'Authentication failed')
      }
    }

    run()
  }, [searchParams, navigate])

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center space-y-4">
          <p className="text-red-600">{error}</p>
          <a href="/login" className="text-indigo-600 underline">
            Back to login
          </a>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center">
      <p className="text-muted-foreground">Completing sign-in…</p>
    </div>
  )
}
